import { Button } from "@/components/base/button";
import { Input } from "@/components/base/input";
import { cn } from "@/lib/utils";
import { QuoteCalculator } from "@/models";
import { CrewRates } from "@/models/crew-rates";
import { LineItem, QuoteSection } from "@/models/line-item";
import { MaterialFormula } from "@/models/material-formula";
import { User } from "@/models/user";
import { WorkOrderType } from "@/models/work-order";
import { UsersService } from "@/services/user";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  TrashIcon,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import React from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "../base";
import { PriceSummary } from "./price-summary";

interface QuoteSectionProps {
  section: QuoteSection;
  lineItems: LineItem[];
  workOrderTypes: WorkOrderType[];
  crews?: User[];
  onSectionNameChange: (trackingId: string, name: string) => void;
  onSectionDelete: (trackingId: string) => void;
  onSectionMoveUp: (trackingId: string) => void;
  onSectionMoveDown: (trackingId: string) => void;
  onSectionCollapse: (trackingId: string) => void;
  onSectionWorkTypeChange: (
    trackingId: string,
    workType: WorkOrderType
  ) => void;
  onSectionCrewChange?: (trackingId: string, crewId: number | null) => void;
  children: React.ReactNode;
  hideMoveUpButton?: boolean;
  hideMoveDownButton?: boolean;
  hideDeleteButton?: boolean;
  taxRate?: number;
  additionalMarkup?: number;
  materialFormulas?: MaterialFormula[];
  quoteCalculator?: QuoteCalculator | null;
  companyName?: string | null;
  lat?: number;
  lon?: number;
  apiUrl?: string;
  apiStage?: string;
}

export function QuoteSectionComponent({
  section,
  lineItems,
  workOrderTypes,
  crews = [],
  onSectionNameChange,
  onSectionDelete,
  onSectionMoveUp,
  onSectionMoveDown,
  onSectionCollapse,
  onSectionWorkTypeChange,
  onSectionCrewChange,
  children,
  hideMoveUpButton,
  hideMoveDownButton,
  hideDeleteButton,
  taxRate = 0,
  additionalMarkup = 0,
  materialFormulas = [],
  quoteCalculator = null,
  apiUrl = "",
  apiStage = "",
}: Readonly<QuoteSectionProps>) {
  const [editedName, setEditedName] = React.useState(section.name);
  const [crewRates, setCrewRates] = React.useState<CrewRates | null>(null);
  const nameInputRef = React.useRef<HTMLInputElement>(null);

  const usersService = React.useMemo(
    () => new UsersService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );

  const fetchCrewRates = React.useCallback(
    async (crewId: number | null) => {
      let crewRates = null;
      if (crewId) {
        try {
          crewRates = await usersService.getCrewRatesByUserId(crewId);
        } catch (error) {
          console.error("Failed to fetch crew rates:", error);
        }
      }
      setCrewRates(crewRates);
    },
    [usersService]
  );

  // Fetch crew rates when section crew changes
  React.useEffect(() => {
    fetchCrewRates(section.crewId);
  }, [fetchCrewRates, section.crewId]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setEditedName(newName);
    onSectionNameChange(section.trackingId, newName);
  };

  const handleConfirmDelete = () => {
    onSectionDelete(section.trackingId);
  };

  const handleMoveUp = () => {
    onSectionMoveUp(section.trackingId);
  };

  const handleMoveDown = () => {
    onSectionMoveDown(section.trackingId);
  };

  const handleCollapse = () => {
    onSectionCollapse(section.trackingId);
  };

  const handleWorkTypeChange = (value: string) => {
    onSectionWorkTypeChange(section.trackingId, value as WorkOrderType);
  };

  const handleCrewChange = async (value: string) => {
    const crewId = value === "none" ? null : parseInt(value, 10);
    onSectionCrewChange?.(section.trackingId, crewId);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
      className="mb-6 border rounded-md"
    >
      <div className="flex items-center p-3 bg-slate-100 rounded-t-md">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="w-6 h-6 mr-2 text-slate-500"
          onClick={handleCollapse}
        >
          {section.isCollapsed ? (
            <ChevronRightIcon className="w-4 h-4" />
          ) : (
            <ChevronDownIcon className="w-4 h-4" />
          )}
        </Button>

        <div className="flex items-center flex-1 gap-4">
          <Input
            ref={nameInputRef}
            value={editedName}
            placeholder="Section name"
            onChange={handleNameChange}
          />
          <Select
            value={section.crewId?.toString() || "none"}
            onValueChange={handleCrewChange}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select Crew" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Crew</SelectItem>
              {crews.map((crew) => (
                <SelectItem key={crew.uid} value={crew.uid.toString()}>
                  {crew.ufirstn && crew.ulastn
                    ? `${crew.ufirstn} ${crew.ulastn}`
                    : crew.ulogin || `User ${crew.uid}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={section.wotype || undefined}
            onValueChange={handleWorkTypeChange}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select service" />
            </SelectTrigger>
            <SelectContent>
              {[...workOrderTypes].sort().map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          {section.isCollapsed && (
            <div className="flex items-center justify-center pl-4 text-sm">
              <PriceSummary
                lineItems={lineItems}
                taxRate={taxRate}
                additionalMarkup={additionalMarkup}
                isSection={true}
                isCollapsed={true}
                isInvoice={false}
              />
            </div>
          )}
          {!hideMoveUpButton && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="w-6 h-6 text-slate-500"
              onClick={handleMoveUp}
              title="Move section up"
            >
              <ArrowUpIcon className="w-4 h-4" />
            </Button>
          )}
          {!hideMoveDownButton && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="w-6 h-6 text-slate-500"
              onClick={handleMoveDown}
              title="Move section down"
            >
              <ArrowDownIcon className="w-4 h-4" />
            </Button>
          )}

          {!hideDeleteButton && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="w-6 h-6 text-slate-500"
                        title="Delete section"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="space-y-4">
                        <p className="text-sm">
                          Are you sure you want to delete the section "
                          {section.name}"? This will also delete all line items
                          in this section. This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={(e) => {
                              e.preventDefault();
                              const popover =
                                e.currentTarget.closest('[role="dialog"]');
                              if (popover) {
                                const trigger = document.querySelector(
                                  `[aria-controls="${popover.id}"]`
                                );
                                trigger?.dispatchEvent(
                                  new Event("click", { bubbles: true })
                                );
                              }
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            onClick={handleConfirmDelete}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent>
                    <p>Delete</p>
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      <AnimatePresence>
        <motion.div
          className={cn("transition-all", {
            "h-0 overflow-hidden": section.isCollapsed,
          })}
          animate={{
            height: section.isCollapsed ? 0 : "auto",
            opacity: section.isCollapsed ? 0 : 1,
          }}
          transition={{ duration: 0.2 }}
        >
          <div className="p-3">
            {React.cloneElement(children as React.ReactElement, {
              materialFormulas,
              quoteCalculator,
              crewRates,
            })}

            {/* {apiUrl && apiStage && jobId != null && (
              <SectionAttachments
                apiUrl={apiUrl}
                apiStage={apiStage}
                sectionId={section.id}
                jobId={jobId}
              />
            )} */}

            <div className="grid grid-cols-1 @md:gap-6 @md:grid-cols-4 mt-4 border-t">
              <div></div>
              <div></div>
              <div></div>
              <div className="pt-4">
                <PriceSummary
                  lineItems={lineItems}
                  taxRate={taxRate}
                  additionalMarkup={additionalMarkup}
                  isSection={true}
                  isInvoice={false}
                />
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}
